import './AuthPage.scss';

import { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Input, Modal, Row, message } from 'antd';
import { signupUsingPassword, triggerEmailRegistrationFlow, triggerResendEmailRegistrationFlow } from '../../api/user';
import { AuthOptions } from '../../types/AuthOptions';
import { useNavigate, useSearchParams } from 'react-router-dom';
import RoutePaths from '../../router/RoutePaths';
import logoImage from '../../assets/images/logo/favicon.svg';
import ServerErrorInterface from '../../types/ServerErrorInterface';
import CustomInputLabel from '../../components/UI/CustomInputLabel';
import { GoogleButton } from '../../components/UI/AuthButton/GoogleButton';
import { OrDivider } from '../../components/UI/AuthButton/OrDivider';
import { getAuthorisationURLWithQueryParamsAndSetState } from 'supertokens-web-js/recipe/thirdparty';
import { getWebsiteDomain } from '../../authConfig';
import { analytics } from '../../analytics/analytics';
import { AnalyticsEvent } from '../../analytics/analyticsEvent';
import { useAuthStore } from '../../stores/authStore';
import { isTemporaryEmail } from '../../common/utils';

// Same wording as the server's rejection, which still covers the domains not listed here.
const DISPOSABLE_EMAIL_MESSAGE = 'Please use a work email or a non-temporary address to register.';

type OnFinishValues = {
  email: string;
};

type OnFinishPasswordValues = {
  email: string;
  password: string;
  passwordConfirmation: string;
};

export default function SignUp() {
  const [form] = Form.useForm();
  const [formPassword] = Form.useForm();
  const { email, setEmail } = useAuthStore();

  const [resendTriggeredTimeout, setResendTriggeredTimeout] = useState(0);
  const [activeAuthMode, setActiveAuthMode] = useState(AuthOptions.MagicLink);
  const navigate = useNavigate();
  const [singupErrorMessages, setSignupErrorMessages] = useState<Record<string, string>[]>([]);

  const onFinish = async (values: OnFinishValues) => {
    try {
      const response = await triggerEmailRegistrationFlow(values.email);
      if (response?.status === 'OK') {
        setEmail(values.email);
        showModal();
      } else {
        void message.error('Something went wrong. Please try again later.');
      }
    } catch (err: any) {
      if (err.isSuperTokensGeneralError === true) {
        void message.error(err.message);
      } else {
        void message.error('Oops! Something went wrong.');
      }
    }
  };

  const onFinishPassword = async (values: OnFinishPasswordValues) => {
    setSignupErrorMessages([]);
    // Trigger API call
    try {
      const res = await signupUsingPassword(values.email, values.password, values.passwordConfirmation);

      // TODO: Filter out if user already belongs to an org or not. In case user does belong to an org do not trigger
      //       Initial onboarding. Check his state and send him to corresponding page.
      navigate(RoutePaths.LoginSelectOrganization);

      setEmail('');
      // TODO: put this into a nice type
    } catch (e) {
      console.log('error: ', e);
      const typedError = e as ServerErrorInterface;

      const newErrors = [];

      if (typedError.response.status === 400) {
        if (typedError.response.data.code === 'ERR-USER-11') {
          newErrors.push({
            message: 'Passwords do not match',
            description: 'Password and password confirmation do not match. Please try again.',
          });
        } else if (typedError.response.data.code === 'ERR-USER-12') {
          newErrors.push({
            message: 'Email already exists',
            description: 'User with given email already exists. Login or Reset your password if you have forgotten it.',
          });
        } else {
          newErrors.push({
            message: 'Unknown error',
            description: 'Unknown error occurred. Please try again later.',
          });
        }
      } else if (typedError.response.status === 500) {
        newErrors.push({
          message: 'Internal server error',
          description:
            'Something is not working on our end. We apologize for the inconvenience. Please try again later.',
        });
      }

      setSignupErrorMessages([...newErrors]);
    }
  };

  const resendRequest = async (email: string) => {
    try {
      await triggerResendEmailRegistrationFlow();
      triggerResendCounter();
    } catch (err: any) {
      if (err.isSuperTokensGeneralError === true) {
        void message.error(err.message);
      } else {
        void message.error('Oops! Something went wrong.');
      }
    }
  };

  const triggerResendCounter = () => {
    setResendTriggeredTimeout(60);

    const intervalId = setInterval((resendTriggeredTimeout: number) => {
      setResendTriggeredTimeout((resendTriggeredTimeout) => {
        return resendTriggeredTimeout - 1;
      });
      if (resendTriggeredTimeout <= 0) {
        clearInterval(intervalId);
      }
    }, 1000);
  };

  const onFinishFailed = (errorInfo: unknown) => {
    console.log('Failed:', errorInfo);
    // TODO: Throw an error using message API.
  };

  const onGoogleSignUp = async () => {
    try {
      const authUrl = await getAuthorisationURLWithQueryParamsAndSetState({
        thirdPartyId: 'google',
        frontendRedirectURI: `${getWebsiteDomain()}/auth/callback/google`,
      });
      // we redirect the user to google for auth.
      window.location.assign(authUrl);
    } catch (err: any) {
      if (err.isSuperTokensGeneralError === true) {
        // this may be a custom error message sent from the API by you.
        void message.error(err.message);
      } else {
        void message.error('Oops! Something went wrong.');
      }
    }
  };

  const [openModal, setOpenModal] = useState(false);

  const [searchParams] = useSearchParams();

  useEffect(() => {
    const linkAuthMode = searchParams.get('mode');
    if (linkAuthMode && linkAuthMode === 'password') {
      setActiveAuthMode(AuthOptions.Password);
    } else if (linkAuthMode && linkAuthMode === 'magicLink') {
      setActiveAuthMode(AuthOptions.MagicLink);
    }

    analytics.track(AnalyticsEvent.screenOpened, { screen: 'signup' });
  }, []);

  const showModal = () => {
    setOpenModal(true);
  };

  // const handleOk = () => {
  //   setModalLoading(true);
  //   setTimeout(() => {
  //     setModalLoading(false);
  //     setOpenModal(false);
  //   }, 3000);
  // };

  const handleCancel = () => {
    setOpenModal(false);
  };

  const SignUpForm = ({ mode }: { mode: AuthOptions }) => {
    // if (false && mode === AuthOptions.MagicLink) {
    if (mode === AuthOptions.MagicLink) {
      return (
        <div className="flex-col flex justify-stretch items-stretch">
          <Form
            form={form}
            name="magic-link-form"
            // labelCol={{ span: 8 }}
            // wrapperCol={{ span: 16 }}
            initialValues={{ remember: true, email }}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            // Clear the email error as soon as the user resumes typing, so the validation
            // message goes away until the next blur or submit (ENTER). #955
            onValuesChange={(changed) => {
              if ('email' in changed) form.setFields([{ name: 'email', errors: [] }]);
            }}
            className="signup-form"
            layout="vertical"
          >
            <CustomInputLabel>Email</CustomInputLabel>
            <Form.Item
              name="email"
              // Validate on blur (and on submit), not on every keystroke, so the
              // "not a valid email" error doesn't flash on the first character. #955
              validateTrigger="onBlur"
              rules={[
                {
                  required: true,
                  message: 'Please input your email!',
                },
                {
                  type: 'email',
                  message: 'Email is not valid',
                },
                {
                  validator: (_, value?: string) =>
                    value && isTemporaryEmail(value)
                      ? Promise.reject(new Error(DISPOSABLE_EMAIL_MESSAGE))
                      : Promise.resolve(),
                },
              ]}
              wrapperCol={{ sm: 24 }}
            >
              <Input placeholder="Enter your work email" className="h-12" />
            </Form.Item>

            <Form.Item
            // wrapperCol={{ sm: 24 }}
            // className={'flex justify-center'}
            >
              <Button
                type="primary"
                htmlType="submit"
                shape="round"
                className="auth-submit-button text-base text-brand-white font-figtree font-semibold"
                style={
                  {
                    // maxWidth: '320px',
                    // minWidth: '250px',
                  }
                }
                block
              >
                Continue
              </Button>
            </Form.Item>
          </Form>
          <OrDivider />
          <GoogleButton onClick={onGoogleSignUp} />
          <SignUpAndContinue />
        </div>
      );
      // } else if (true || mode === AuthOptions.Password) {
    } else if (mode === AuthOptions.Password) {
      return (
        <Col span={8}>
          <Form
            form={formPassword}
            name="passowrd-form"
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            initialValues={{ remember: true, email }}
            onFinish={onFinishPassword}
            onFinishFailed={onFinishFailed}
            // Clear the email error as soon as the user resumes typing, so the validation
            // message goes away until the next blur or submit (ENTER). #955
            onValuesChange={(changed) => {
              if ('email' in changed) formPassword.setFields([{ name: 'email', errors: [] }]);
            }}
            className="signup-form"
            layout="vertical"
          >
            <Form.Item
              name="email"
              // Validate on blur (and on submit), not on every keystroke, so the
              // "not a valid email" error doesn't flash on the first character. #955
              validateTrigger="onBlur"
              rules={[
                {
                  required: true,
                  message: 'Please input your email!',
                },
                {
                  type: 'email',
                  message: 'Email is not valid',
                },
                {
                  validator: (_, value?: string) =>
                    value && isTemporaryEmail(value)
                      ? Promise.reject(new Error(DISPOSABLE_EMAIL_MESSAGE))
                      : Promise.resolve(),
                },
              ]}
              wrapperCol={{ sm: 24 }}
            >
              <Input placeholder="Enter your work email" className="h-12" />
            </Form.Item>

            <Form.Item
              name={'password'}
              rules={[
                {
                  required: true,
                  message: 'Password field is required!',
                },
                {
                  type: 'string',
                },
              ]}
              wrapperCol={{ sm: 24 }}
            >
              <Input.Password placeholder="Insert your password" className="h-12" />
            </Form.Item>

            <Form.Item
              name={'passwordConfirmation'}
              rules={[
                {
                  required: true,
                  message: 'Password confirmation field is required!',
                },
                {
                  type: 'string',
                },
              ]}
              wrapperCol={{ sm: 24 }}
            >
              <Input.Password placeholder="Repeat your password" className="h-12" />
            </Form.Item>

            <Form.Item wrapperCol={{ sm: 24 }} className={'flex justify-center'}>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                shape="round"
                className="auth-submit-button"
                style={{
                  maxWidth: '320px',
                  minWidth: '250px',
                }}
              >
                Sign up
              </Button>
            </Form.Item>
          </Form>
          <SignUpAndContinue />
        </Col>
      );
    }
    return <></>;
  };

  return (
    <>
      <div className="auth-page-wrapper">
        <h2 className="logo-image text-center flex align-middle justify-center">
          <img src={logoImage} alt="Logo" style={{ height: '2rem', marginRight: '0.25rem' }} /> Taskonio
        </h2>
        <h1 className="mb-8">Sign up for free </h1>

        <Row justify={'center'} align={'middle'} className={'mb-6'}>
          {(() => {
            if (singupErrorMessages.length > 0) {
              return (
                <>
                  {singupErrorMessages.map((error, i) => {
                    return (
                      <Alert
                        key={i}
                        message={error.message}
                        description={error.description}
                        type="error"
                        showIcon
                        closable
                      />
                    );
                  })}
                </>
              );
            } else {
              return <></>;
            }
          })()}
        </Row>

        <Row justify={'center'} align={'middle'}>
          <SignUpForm mode={activeAuthMode} />
        </Row>

        <div className="text-base text-brand-grey-dark font-figtree font-semibold flex flex-col justify-center items-center mt-16">
          Already have an account?
          <Button
            className="text-base text-blue-600 font-figtree font-semibold py-2 px-1"
            type="link"
            onClick={() => {
              const emailValue = form.getFieldValue('email') || formPassword.getFieldValue('email');
              if (emailValue) setEmail(emailValue);
              navigate(RoutePaths.Login);
            }}
          >
            Login here
          </Button>
        </div>

        <Modal
          open={openModal}
          // onOk={handleOk}
          onCancel={handleCancel}
          footer={null}
        >
          <div className="flex flex-col items-center">
            <div className="text-center text-brand-grey-dark text-[32px] font-semibold font-['Figtree'] ">
              Check your email
            </div>

            <div className="w-96 text-center mt-3">
              <span className="text-brand-grey-dark text-base font-normal font-['Figtree']">
                We&apos;ve sent you the sign up link to{' '}
              </span>
              <span className="text-brand-grey-dark text-base font-semibold font-['Figtree']">{email}</span>
            </div>
            <div className="w-96 text-center text-brand-grey-dark text-base font-normal font-['Figtree'] mt-3">
              Please check your email inbox and spam folder to make sure you receive it.
            </div>
            <svg
              className="mt-10 mb-10"
              xmlns="http://www.w3.org/2000/svg"
              width="91"
              height="60"
              viewBox="0 0 91 60"
              fill="none"
            >
              <rect width="90.3614" height="60" rx="5.78313" fill="#08CF65" />
              <path
                d="M50.1759 34.0348L88.0335 9.29494C92.3478 6.47561 90.2685 0 85.0491 0H5.31228C0.0725292 0 -1.99233 6.51672 2.36042 9.31608L40.8474 34.068C43.6726 35.885 47.3649 35.8718 50.1759 34.0348Z"
                fill="#1B4349"
              />
            </svg>
            <div className="text-center text-brand-grey-dark text-base font-semibold font-['Figtree']">
              Haven&apos;t gotten the email?
            </div>
            <div className="text-center mt-[-4px]">
              {resendTriggeredTimeout > 0 ? (
                <p>You can send next request in {resendTriggeredTimeout} seconds.</p>
              ) : (
                <>
                  <Button
                    type="link"
                    className="p-0 m-0"
                    onClick={async () => {
                      await resendRequest(email);
                    }}
                  >
                    <span className=" text-base font-medium font-['Figtree'] underline">Resend the link</span>
                  </Button>
                  <span className="text-blue-600 text-base font-medium font-['Figtree']"> or </span>

                  <Button type="link" className="p-0 m-0" onClick={handleCancel}>
                    <span className=" text-base font-medium font-['Figtree'] underline">edit the email address</span>
                  </Button>
                </>
              )}
            </div>
            <Button type="text" block className="mt-10" onClick={handleCancel}>
              <div className="text-center text-brand-grey-dark text-base font-semibold font-['Figtree']">Back to sign up</div>
            </Button>
          </div>
        </Modal>
      </div>
    </>
  );
}

const SignUpAndContinue = () => {
  const handleClickOnTerms = () => {
    window.open('https://taskonio.com/legal/', '_blank', 'noopener,noreferrer');
  };
  return (
    <div className="w-80 text-center">
      <span className="text-gray-400 text-xs font-normal font-['Figtree']">
        By signing up, you agree to Taskonio&apos;s{' '}
      </span>
      <span
        className="text-gray-400 text-xs font-normal font-['Figtree'] underline cursor-pointer"
        onClick={handleClickOnTerms}
      >
        Terms and Privacy
      </span>
      <span className="text-gray-400 text-xs font-normal font-['Figtree']">.</span>
    </div>
  );
};
